import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import {
  ButtonComponent, FieldComponent, ModalComponent,
  SelectComponent, type SelectOption,
} from '@echospaces-web-monorepo/es-shared-ui';

import { OrgUserPickerComponent } from '../users/org-user-picker.component';
import type { OrgMember } from '../users/company-members.service';
import type { SpaceRole } from './space-members.service';

const ROLE_OPTIONS: SelectOption[] = [
  { value: 'visitor', label: 'Visitor — read-only attendance' },
  { value: 'editor', label: 'Editor — link/unlink + member list' },
  { value: 'admin', label: 'Admin — full subtree control + sub-delegation' },
];

/**
 * Modal to grant a per-space role to a user. The picker resolves a user
 * inside the caller's organization (no cross-org grants) — paste-UUID is
 * gone now that the org-scoped directory is available. Server-side
 * `addMember` is upsert semantics, so this also re-roles existing members.
 */
@Component({
  selector: 'es-add-member-modal',
  imports: [
    FormsModule, ModalComponent, ButtonComponent, SelectComponent,
    FieldComponent, OrgUserPickerComponent,
  ],
  template: `
    <es-modal [open]="open" title="Add member" width="sm" (closed)="onCancel()">
      <div class="p-6 space-y-4">
        <es-field
          label="User"
          hint="Search by name or email — only members of your organization are listed."
          [error]="userError()"
        >
          <es-org-user-picker
            (userSelected)="onUserSelected($event)"
          ></es-org-user-picker>
        </es-field>

        <es-field label="Role">
          <es-select [options]="roleOptions" [(ngModel)]="role"></es-select>
        </es-field>

        @if (submitError()) {
          <p class="text-sm text-red-400">{{ submitError() }}</p>
        }

        <div class="flex justify-end gap-2 pt-2">
          <es-button variant="ghost" (clicked)="onCancel()">Cancel</es-button>
          <es-button
            variant="primary"
            [disabled]="submitting() || !selectedUser()"
            (clicked)="onSubmit()"
          >
            {{ submitting() ? 'Adding…' : 'Add member' }}
          </es-button>
        </div>
      </div>
    </es-modal>
  `,
})
export class AddMemberModalComponent {
  @Input() open = false;
  @Output() closed = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<{ userId: string; role: SpaceRole }>();

  readonly roleOptions = ROLE_OPTIONS;

  role: SpaceRole = 'editor';

  readonly selectedUser = signal<OrgMember | null>(null);
  readonly submitting = signal(false);
  readonly userError = signal<string | null>(null);
  readonly submitError = signal<string | null>(null);

  onUserSelected(member: OrgMember): void {
    this.selectedUser.set(member);
    this.userError.set(null);
  }

  onSubmit(): void {
    const user = this.selectedUser();
    if (!user) {
      this.userError.set('Pick a member from the list');
      return;
    }
    this.submitting.set(true);
    this.submitted.emit({ userId: user.userId, role: this.role });
  }

  /**
   * Parent calls this after `submitted` finishes (success or failure) so
   * the modal can reset its busy state. Keeps state ownership simple —
   * the parent is the source of truth for "is the request in flight".
   */
  finishSubmit(error?: string): void {
    this.submitting.set(false);
    this.submitError.set(error ?? null);
    if (!error) {
      this.selectedUser.set(null);
      this.role = 'editor';
      this.closed.emit();
    }
  }

  onCancel(): void {
    this.userError.set(null);
    this.submitError.set(null);
    this.selectedUser.set(null);
    this.closed.emit();
  }
}
